/**
 * SequenceManager - manages reserved sequence number blocks for offline
 * document creation.
 *
 * On first connect, the client requests a block of 1000 sequence numbers
 * per document type from the server. While offline, provisional document
 * numbers are generated from this pool using a DRAFT- prefix. When the
 * client syncs, the server renumbers them with real sequence numbers.
 */

const SEQUENCES_KEY = 'lc:sequences'
const CLIENT_ID_KEY = 'lc:client_id'

export type DocumentType = 'invoice' | 'quote' | 'credit_note' | 'purchase_order' | 'expense_claim'

export const DOCUMENT_TYPES: DocumentType[] = [
  'invoice', 'quote', 'credit_note', 'purchase_order', 'expense_claim',
]

interface ReservedBlock {
  start: number
  count: number
  prefix: string
  used: number
}

type ReservedBlocks = Partial<Record<DocumentType, ReservedBlock>>

export class SequenceManager {
  /** Get or create a stable client ID for this browser tab/device. */
  getClientId(): string {
    let id = localStorage.getItem(CLIENT_ID_KEY)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(CLIENT_ID_KEY, id)
    }
    return id
  }

  /** Get the short client ID prefix (first 8 chars) for display. */
  getClientShortId(): string {
    return this.getClientId().slice(0, 8)
  }

  /**
   * Store reserved blocks received from the server.
   * Merges with existing blocks (for replenishment).
   */
  storeBlocks(blocks: Record<string, { start: number; count: number; prefix: string }>): void {
    const current = this.loadBlocks()
    for (const [type, block] of Object.entries(blocks)) {
      current[type as DocumentType] = { ...block, used: 0 }
    }
    this.saveBlocks(current)
  }

  /**
   * Get the next provisional document number for the given type.
   * Returns null if the block is exhausted.
   */
  nextNumber(type: DocumentType): string | null {
    const blocks = this.loadBlocks()
    const block = blocks[type]
    if (!block || block.used >= block.count) return null

    const seq = block.start + block.used
    block.used++
    this.saveBlocks(blocks)

    const paddedSeq = seq.toString().padStart(3, '0')
    return `DRAFT-${block.prefix.replace('DRAFT-', '')}-${paddedSeq}`
  }

  /** How many numbers remain for the given type. */
  remaining(type: DocumentType): number {
    const blocks = this.loadBlocks()
    const block = blocks[type]
    if (!block) return 0
    return Math.max(0, block.count - block.used)
  }

  /** Whether the block for a type is exhausted or absent. */
  isExhausted(type: DocumentType): boolean {
    return this.remaining(type) === 0
  }

  /** Whether any type needs replenishment (< 100 remaining). */
  needsReplenishment(): boolean {
    return DOCUMENT_TYPES.some((type) => this.remaining(type) < 100)
  }

  /** Whether any blocks have been reserved at all. */
  hasBlocks(): boolean {
    const blocks = this.loadBlocks()
    return DOCUMENT_TYPES.some((type) => blocks[type] !== undefined)
  }

  /** Check if a document number is a provisional (offline-generated) number. */
  static isProvisional(number: string): boolean {
    return number.startsWith('DRAFT-')
  }

  /** Clear all stored blocks. */
  clear(): void {
    localStorage.removeItem(SEQUENCES_KEY)
  }

  // --- Persistence ---

  private loadBlocks(): ReservedBlocks {
    const raw = localStorage.getItem(SEQUENCES_KEY)
    if (!raw) return {}
    try {
      return JSON.parse(raw) as ReservedBlocks
    } catch {
      return {}
    }
  }

  private saveBlocks(blocks: ReservedBlocks): void {
    localStorage.setItem(SEQUENCES_KEY, JSON.stringify(blocks))
  }
}
